import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  EditOrderGuideActionType,
  EditOrderGuideDispatch,
  useEditOrderGuideReducer,
} from '@/experiences/sales-portal/domain/order-guides/blocs/reducers/use-edit-order-guide-reducer';
import {
  EditOrderGuideState,
  OrderGuide,
  OrderGuidePartition,
  SelectedOrderGuideItem,
} from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { OrderGuideEditability } from '@/experiences/sales-portal/domain/order-guides/blocs/utils/editability-utils';
import { validatePartitionName } from '@/experiences/sales-portal/domain/order-guides/blocs/utils/naming-utils';

const EditOrderGuideStateContext = createContext<EditOrderGuideState | null>(
  null,
);

const EditOrderGuideActionsContext =
  createContext<EditOrderGuideDispatch | null>(null);

const SelectPartitionContext = createContext<
  ((partitionUUID: string) => void) | null
>(null);

const OrderGuideEditabilityContext = createContext<
  OrderGuideEditability | undefined
>(undefined);

const SelectedItemsContext = createContext<SelectedOrderGuideItem[] | null>(
  null,
);

/**
 * Returns the current edit-order-guide state (the loaded guide plus the
 * transient `selectedPartitionUUID`).
 *
 * @throws if called outside an `EditOrderGuideContextProvider`.
 */
export const useEditOrderGuideState = (): EditOrderGuideState => {
  const context = useContext(EditOrderGuideStateContext);
  if (!context) {
    throw new Error(
      'useEditOrderGuideState requires a EditOrderGuideContextProvider',
    );
  }

  return context;
};

/**
 * Returns the reducer dispatch for editing the open guide (e.g. par levels).
 *
 * @throws if called outside an `EditOrderGuideContextProvider`.
 */
export const useEditOrderGuideActions = (): EditOrderGuideDispatch => {
  const context = useContext(EditOrderGuideActionsContext);
  if (!context) {
    throw new Error(
      'useEditOrderGuideActions requires a EditOrderGuideContextProvider',
    );
  }

  return context;
};

interface PartitionSelection {
  selectedPartition: OrderGuidePartition | null;
  selectPartition: (partitionUUID: string) => void;
}

/**
 * Returns the selected partition and a setter. Selection falls back to the
 * default partition when none is explicitly selected, and `null` only if the
 * guide has no partitions.
 *
 * @throws if called outside an `EditOrderGuideContextProvider`.
 */
export const usePartitionSelection = (): PartitionSelection => {
  const { partitions, selectedPartitionUUID } = useEditOrderGuideState();
  const selectPartition = useContext(SelectPartitionContext);
  if (!selectPartition) {
    throw new Error(
      'usePartitionSelection requires a EditOrderGuideContextProvider',
    );
  }

  const selectedPartition =
    partitions.find(
      (partition) => partition.partitionUUID === selectedPartitionUUID,
    ) ??
    partitions.find((partition) => partition.isDefault) ??
    null;

  return { selectedPartition, selectPartition };
};

/**
 * Returns a validator for new partition (group) names, checked against the
 * open guide's current partitions. Rejects the reserved default name and
 * duplicates.
 */
export const useValidatePartitionName = (): ((name: string) => boolean) => {
  const { partitions } = useEditOrderGuideState();

  return useCallback(
    (name: string) =>
      validatePartitionName(
        name,
        partitions.flatMap((partition) =>
          partition.name === null ? [] : [partition.name],
        ),
      ),
    [partitions],
  );
};

/**
 * Returns a setter for one entry's par level, addressed by its
 * (partition, variant pack).
 */
export const useSetItemParLevel = (): ((
  partitionUUID: string,
  variantPackUUID: string,
  parLevel: number | null,
) => void) => {
  const dispatch = useEditOrderGuideActions();

  return useCallback(
    (partitionUUID, variantPackUUID, parLevel) =>
      dispatch({
        payload: { parLevel, partitionUUID, variantPackUUID },
        type: EditOrderGuideActionType.SET_ITEM_PAR_LEVEL,
      }),
    [dispatch],
  );
};

/**
 * Returns the items currently selected in the open guide.
 *
 * @throws if called outside an `EditOrderGuideContextProvider`.
 */
export const useSelectedItems = (): SelectedOrderGuideItem[] => {
  const context = useContext(SelectedItemsContext);
  if (!context) {
    throw new Error(
      'useSelectedItems requires a EditOrderGuideContextProvider',
    );
  }

  return context;
};

/** Returns a toggler for one variant pack's selection. */
export const useSelectItem = (): ((
  selection: SelectedOrderGuideItem,
) => void) => {
  const dispatch = useEditOrderGuideActions();

  return useCallback(
    (selection) =>
      dispatch({
        payload: selection,
        type: EditOrderGuideActionType.SELECT_ITEM,
      }),
    [dispatch],
  );
};

/** Returns a setter that replaces one partition's selected packs (select-all/clear). */
export const useSetPartitionSelection = (): ((
  partitionUUID: string,
  packs: Omit<SelectedOrderGuideItem, 'partitionUUID'>[],
) => void) => {
  const dispatch = useEditOrderGuideActions();

  return useCallback(
    (partitionUUID, packs) =>
      dispatch({
        payload: { packs, partitionUUID },
        type: EditOrderGuideActionType.SET_PARTITION_SELECTION,
      }),
    [dispatch],
  );
};

/** Returns a deleter that removes the given variant packs from the guide. */
export const useDeleteItems = (): ((
  items: SelectedOrderGuideItem[],
) => void) => {
  const dispatch = useEditOrderGuideActions();

  return useCallback(
    (items) =>
      dispatch({
        payload: { items },
        type: EditOrderGuideActionType.DELETE_ITEMS,
      }),
    [dispatch],
  );
};

/**
 * Returns the open guide's editability (read-only / locked state).
 *
 * @throws if called outside an `EditOrderGuideContextProvider`.
 */
export const useCurrentOrderGuideEditability = (): OrderGuideEditability => {
  const context = useContext(OrderGuideEditabilityContext);
  if (context === undefined) {
    throw new Error(
      'useCurrentOrderGuideEditability requires a EditOrderGuideContextProvider',
    );
  }

  return context;
};

interface Props {
  children?: React.ReactNode;
  editability: OrderGuideEditability;
  initialSelectedPartitionUUID?: string;
  orderGuide: OrderGuide;
}

export const EditOrderGuideContextProvider: React.FC<Props> = ({
  children,
  editability,
  initialSelectedPartitionUUID,
  orderGuide,
}) => {
  const [orderGuideState, dispatch] = useEditOrderGuideReducer(orderGuide);
  const [selectedPartitionUUID, setSelectedPartitionUUID] = useState<
    string | null
  >(initialSelectedPartitionUUID ?? null);

  const state = useMemo<EditOrderGuideState>(
    () => ({ ...orderGuideState.guide, selectedPartitionUUID }),
    [orderGuideState.guide, selectedPartitionUUID],
  );

  return (
    <OrderGuideEditabilityContext.Provider value={editability}>
      <SelectPartitionContext.Provider value={setSelectedPartitionUUID}>
        <EditOrderGuideActionsContext.Provider value={dispatch}>
          <EditOrderGuideStateContext.Provider value={state}>
            <SelectedItemsContext.Provider
              value={orderGuideState.selectedItems}
            >
              {children}
            </SelectedItemsContext.Provider>
          </EditOrderGuideStateContext.Provider>
        </EditOrderGuideActionsContext.Provider>
      </SelectPartitionContext.Provider>
    </OrderGuideEditabilityContext.Provider>
  );
};
